import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Trash2, RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';
import { vocabularyAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const VocabularyCleanup = () => {
  const [cleaning, setCleaning] = useState(false);
  const [cleanupResults, setCleanupResults] = useState(null);
  const { toast } = useToast();

  const handleCleanup = async () => {
    setCleaning(true);
    setCleanupResults(null);

    try {
      // Use the thorough database reset endpoint
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/admin/reset-vocabulary`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Reset failed: ${response.statusText}`);
      }

      const result = await response.json();

      setCleanupResults({
        deleted: result.deleted_vocabulary,
        errors: 0,
        errorDetails: []
      });

      toast({
        title: "Database Reset Complete",
        description: `Completely removed ${result.deleted_vocabulary} words. Database is now clean.`,
      });

    } catch (error) {
      toast({
        title: "Database Reset Failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Card className="shadow-lg border-orange-200">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 text-orange-700">
            <AlertTriangle className="w-6 h-6" />
            <span>Clean Database - Keep Only Your Vocabulary</span>
          </CardTitle>
          <p className="text-gray-600">
            Remove all mock/external vocabulary and prepare for your authentic Algerian content only
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Warning */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              <span className="font-semibold text-orange-800">Important</span>
            </div>
            <p className="text-orange-700 text-sm">
              This will delete ALL current vocabulary (including mock data) to make room for only your authentic Algerian vocabulary.
              Make sure you have your vocabulary list ready to re-import after cleanup.
            </p>
          </div>

          {/* Cleanup Button */}
          <div className="text-center">
            <Button 
              onClick={handleCleanup}
              disabled={cleaning}
              size="lg"
              variant="destructive"
              className="px-8 py-3"
            >
              {cleaning ? (
                <>
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                  Cleaning Database...
                </>
              ) : (
                <>
                  <Trash2 className="w-5 h-5 mr-2" />
                  Clean Database
                </>
              )}
            </Button>
          </div>

          {/* Cleanup Results */}
          {cleanupResults && (
            <div className="space-y-4">
              <Card className="border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-green-800">Cleanup Complete</span>
                  </div>
                  <div className="text-sm text-green-700">
                    <div>• Deleted {cleanupResults.deleted} vocabulary words</div>
                    <div>• Database is now ready for your authentic content</div>
                    {cleanupResults.errors > 0 && (
                      <div className="text-red-600">• {cleanupResults.errors} errors occurred</div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Next Steps */}
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-blue-800 mb-2">Next Steps:</h3>
                  <div className="space-y-1 text-sm text-blue-700">
                    <div>1. Go to Vocabulary Import page</div>
                    <div>2. Import your authentic Algerian vocabulary</div>
                    <div>3. Verify all words are spelled correctly</div>
                    <div>4. Start using games with your authentic content</div>
                  </div>
                  <div className="mt-3">
                    <Button 
                      onClick={() => window.location.href = '/vocabulary/import'}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Go to Import Page
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Error Details */}
              {cleanupResults.errors > 0 && (
                <Card className="border-red-200">
                  <CardHeader>
                    <CardTitle className="text-red-700">Cleanup Errors</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1 max-h-40 overflow-y-auto text-sm text-red-600">
                      {cleanupResults.errorDetails.map((error, index) => (
                        <div key={index}>{error}</div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VocabularyCleanup;